/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useCallback, useEffect, useRef, useState } from "react";
import * as drive from "../../../api/drive";

/**
 * Everything the drive browser needs for one folder: its contents, the
 * breadcrumb trail, and the mutations that operate on it.
 *
 * `folderId === null` is the root. The API has no root-folder row, so the trail
 * is built by walking `parent_folder_id` upward from the current folder.
 */
export const useDrive = (folderId) => {
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [trail, setTrail] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Guards against a slow response for a folder the user has already left
  // overwriting the contents of the one they are now looking at.
  const requestRef = useRef(0);

  const buildTrail = useCallback(async (id) => {
    const crumbs = [];
    let cursor = id;
    // Bounded so a cyclic parent chain from bad data cannot hang the UI.
    for (let depth = 0; cursor && depth < 32; depth += 1) {
      const { folder } = await drive.getFolder(cursor);
      if (!folder) break;
      crumbs.unshift({ id: folder.id, name: folder.folder_name });
      cursor = folder.parent_folder_id;
    }
    return crumbs;
  }, []);

  const load = useCallback(async () => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;

    setLoading(true);
    setError(null);
    try {
      const [folderResult, fileResult, crumbs] = await Promise.all([
        drive.listFolders({ parentFolderId: folderId }),
        drive.listFiles({ folderId }),
        folderId ? buildTrail(folderId) : Promise.resolve([]),
      ]);
      if (requestRef.current !== requestId) return;
      setFolders(folderResult.folders);
      setFiles(fileResult.files);
      setTrail(crumbs);
    } catch (err) {
      if (requestRef.current !== requestId) return;
      setError(err.message || "Failed to load this folder.");
    } finally {
      if (requestRef.current === requestId) setLoading(false);
    }
  }, [folderId, buildTrail]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    folders,
    files,
    trail,
    loading,
    error,
    reload: load,
    createFolder: (folderName) =>
      drive.createFolder({ folderName, parentFolderId: folderId }).then(load),
    // Renames patch state from the mutation's own response instead of calling
    // load(), which would re-fetch both listings AND walk the whole breadcrumb
    // chain again — up to 32 requests to change one label.
    //
    // Folders re-sort because the server orders them by name and a rename moves
    // the row; files are ordered by created_at, which a rename cannot change.
    renameFolder: (id, folderName) =>
      drive.updateFolder(id, { folderName }).then((folder) =>
        setFolders((prev) =>
          prev
            .map((f) => (f.id === id ? { ...f, ...folder } : f))
            .sort((a, b) => a.folder_name.localeCompare(b.folder_name))
        )
      ),
    renameFile: (id, fileName) =>
      drive.updateFile(id, { fileName }).then((file) =>
        setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...file } : f)))
      ),
    // Deletes and moves are driven from DrivePage so one reload covers a whole
    // multi-item selection rather than firing per item.
  };
};

/**
 * Fetches a fresh signed URL and hands the browser the download. URLs are
 * short-lived, so one is minted per click rather than cached.
 */
export const downloadFile = async (fileId, fileName) => {
  const { downloadUrl } = await drive.getDownloadUrl(fileId);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = fileName || "";
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
};
